import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Form, Input, Space } from 'antd'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignTopOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Dimension } from '../../../../../features/dimension/types/Dimension'
import DimensionSourceTag from './DimensionSourceTag'
import DimensionInspectorChildren from './DimensionInspectorChildren'
import DimensionFormModal from './DimensionFormModal'
import DimensionFormFields, { DimensionFormValues } from './DimensionFormFields'
import { canRenameOrDelete } from '../utils/dimensionAccess'
import { useDimensionGridActions } from '../hooks/useDimensionGridActions'

interface DimensionInspectorPanelProps {
  dimension: Dimension
  flatList: Dimension[]
  canEdit: boolean
  onClose: () => void
}

const DimensionInspectorPanel: React.FC<DimensionInspectorPanelProps> = ({ dimension, flatList, canEdit, onClose }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<DimensionFormValues>()
  const [addChildOpen, setAddChildOpen] = useState(false)
  const canEditThis = canRenameOrDelete(dimension, canEdit)
  const excludeIds = useMemo(() => [dimension.dimensionId], [dimension.dimensionId])
  const {
    rename,
    reorder,
    moveToEdge,
    isRenaming,
    isReordering,
    isSettingDefault,
    applyParentDimension,
    applyDefault
  } = useDimensionGridActions()

  useEffect(() => {
    form.setFieldsValue({
      name: dimension.name,
      parentDimensionId: dimension.parentDimensionId ?? undefined,
      default: !!dimension.default
    })
  }, [dimension, form])

  const children = flatList.filter(d => d.parentDimensionId === dimension.dimensionId)

  const handleValuesChange = (changed: Partial<DimensionFormValues>) => {
    if (!canEdit) return
    if ('parentDimensionId' in changed) applyParentDimension(flatList, dimension, changed.parentDimensionId || null)
    if ('default' in changed) applyDefault(dimension, !!changed.default)
  }

  const handleNameBlur = () => {
    const value = form.getFieldValue('name')
    if (canEditThis && value?.trim() && value !== dimension.name) rename(dimension, value)
  }

  return (
    <Card
      size="small"
      style={{ width: 320 }}
      title={t('dimensionsPage:inspectorSelectedTitle')}
      extra={<Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />}
    >
      <div style={{ marginTop: 16 }}>
        <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
          <Form.Item label={t('dimensionsPage:tagSourceLabel')}>
            <DimensionSourceTag dimension={dimension} />
          </Form.Item>

          <DimensionFormFields
            flatList={flatList}
            excludeIds={excludeIds}
            disabled={!canEdit}
            isSettingDefault={isSettingDefault}
            nameInputProps={{
              disabled: !canEditThis || isRenaming,
              onBlur: handleNameBlur,
              onPressEnter: handleNameBlur
            }}
          />

          <Form.Item label={t('global:code')}>
            <Input value={dimension.dimensionId} disabled />
          </Form.Item>

          <Form.Item label={t('dimensionsPage:inspectorOrder')}>
            <Space>
              <Input value={dimension.order} disabled style={{ width: 64 }} />
              <Button
                icon={<VerticalAlignTopOutlined />}
                disabled={!canEdit}
                loading={isReordering}
                onClick={() => moveToEdge(flatList, [dimension.dimensionId], 'start')}
              />
              <Button
                icon={<ArrowUpOutlined />}
                disabled={!canEdit}
                loading={isReordering}
                onClick={() => reorder(flatList, dimension.dimensionId, 'up')}
              />
              <Button
                icon={<ArrowDownOutlined />}
                disabled={!canEdit}
                loading={isReordering}
                onClick={() => reorder(flatList, dimension.dimensionId, 'down')}
              />
              <Button
                icon={<VerticalAlignBottomOutlined />}
                disabled={!canEdit}
                loading={isReordering}
                onClick={() => moveToEdge(flatList, [dimension.dimensionId], 'end')}
              />
            </Space>
          </Form.Item>
        </Form>
      </div>

      <DimensionInspectorChildren
        childDimensions={children}
        canEdit={canEdit}
        onAddChild={() => setAddChildOpen(true)}
      />

      <DimensionFormModal
        open={addChildOpen}
        mode="addChild"
        flatList={flatList}
        parentDimensionId={dimension.dimensionId}
        onClose={() => setAddChildOpen(false)}
      />
    </Card>
  )
}

export default DimensionInspectorPanel
